import { ensureNever, noop } from "../../TypingUtils";
import { Channel } from "../Channel";

import { Registry, State } from "./Registry";

export interface Disposable extends NonNullable<unknown> {
    dispose: () => Promise<void>;
}

/**
 * Базовый пул, предоставляющий:
 * - `exec` - безопасный доступ к элементу
 * - `add` - расширение
 * - `evict` - сокращение
 * - `dispose` - уничтожение
 */
export class AsyncPool<Item extends Disposable> {
    private disposed = false;
    private readonly registry = new Registry<Item>();
    private readonly idles = new Channel<Item>();
    private running = 0;
    private readonly destroying = new Set<Promise<void>>();
    private readonly shutdownSignal = Promise.withResolvers<void>();

    /**
     * Добавляет элемент в пул и делает его немедленно доступным.
     *
     * @throws если `item` уже присутствует в пуле.
     */
    public add(item: Item): void {
        this.ensureAlive();
        this.registry.add(item);
        this.idles.write(item);
    }

    /**
     * Уменьшает ёмкость пула на один элемент.
     *
     * - Если есть свободный элемент — удаляет его немедленно.
     * - Если все элементы заняты — помечает один из них `Doomed`:
     *   при следующем `release` он будет отброшен, а не возвращён в пул.
     * - Если пул пуст — не делает ничего.
     *
     * @returns `true`, если ёмкость была уменьшена; `false`, если пул уже пуст.
     */
    public evict(): boolean {
        this.ensureAlive();
        const idle = this.idles.readSync();
        if (idle !== undefined) {
            this.destroy(idle);
            return true;
        }
        const busy = this.registry.findBusy();
        if (busy) {
            this.registry.doom(busy);
            return true;
        }
        return false;
    }

    /**
     * Захватывает свободный элемент пула, вызывает `await fn(item)` и возвращает элемент обратно.
     *
     * - Если нет свободного элемента — ожидает появления (блокирует вызов).
     * - Ожидающие вызовы получают элемент в порядке FIFO.
     * - Ошибка внутри `fn` пробрасывается вызывающей стороне.
     *
     * @remarks
     * Подразумевается, что `fn` всегда `await`-ит I/O-bound операцию,
     * что разрывает цепочку микрозадач и даёт event loop провернуться.
     * Поэтому оборачивание в `setTimeout(resolve, 0)` не производится.
     *
     * @warn
     * Вложенный `exec` на пуле размера 1, вызванный из `fn` с `await`,
     * приведёт к дедлоку: outer ждёт inner, inner ждёт освобождения outer.
     */
    public async exec<R>(fn: (item: Item) => Promise<R>): Promise<R> {
        this.ensureAlive();
        const item = await this.capture();
        try {
            this.running++;
            return await fn(item);
        } finally {
            this.release(item);
            this.running--;
            if (this.disposed && this.running === 0) {
                this.shutdownSignal.resolve();
            }
        }
    }

    public async dispose(): Promise<void> {
        if (!this.disposed) {
            this.disposed = true;
            this.idles.close("Pool disposed");
            this.releaseAll();
        }
        await this.shutdown();
    }

    private releaseAll() {
        const idles = this.registry.doomAll();
        idles.forEach(i => this.release(i));
    }

    private async shutdown(): Promise<void> {
        if (this.running > 0) {
            await this.shutdownSignal.promise;
        }
        await Promise.allSettled(this.destroying);
    }

    private async capture(): Promise<Item> {
        const item = await this.idles.read();
        return this.registry.capture(item);
    }

    private release(item: Item): void {
        const state = this.registry.stateOf(item);
        switch (state) {
            case State.Busy:
                return this.free(item);
            case State.Doomed:
                return this.destroy(item);
            case State.Idle:
                throw new Error("Pool: release not captured");
            default:
                return ensureNever(state);
        }
    }

    private free(item: Item) {
        this.registry.free(item);
        this.idles.write(item);
    }

    private destroy(item: Item) {
        this.registry.remove(item);
        const promise = Promise.try(() => item.dispose());
        this.destroying.add(promise);
        // в destroying нужны лишь незавершенные item.dispose
        promise.catch(noop).finally(() => this.destroying.delete(promise));
    }

    private ensureAlive() {
        if (this.disposed) {
            throw new Error("Pool: interaction on disposed");
        }
    }
}
